/**
 * Página de Monitoramento da Dead Letter Queue (DLQ)
 * 
 * Dashboard para visualizar e gerenciar mensagens que falharam
 * definitivamente no processamento de flows e contratos.
 * 
 * @author Sistema de Flows v3.0 - DLQ + Contratos
 */

import { useState } from 'react';
import { RefreshCw, Skull, Clock, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useQueryClient } from '@tanstack/react-query';
import { DlqStatusCards } from '@/components/dashboard/DlqStatusCards';
import { DlqHealthAlert } from '@/components/dashboard/DlqHealthAlert';
import { DlqReasonChart } from '@/components/dashboard/DlqReasonChart';
import { DlqItemsTable } from '@/components/dashboard/DlqItemsTable';
import { DlqContractsStatusCards } from '@/components/dashboard/DlqContractsStatusCards';
import { DlqContractsItemsTable } from '@/components/dashboard/DlqContractsItemsTable';
import { useDiscardOldDlqItems } from '@/hooks/useDlqMonitor';
import { toast } from 'sonner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';

export default function DlqMonitor() {
  const queryClient = useQueryClient();
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [discardDays, setDiscardDays] = useState('7');
  const [activeTab, setActiveTab] = useState('flows');
  
  const discardOldMutation = useDiscardOldDlqItems();

  // Função para atualizar todos os dados
  const handleRefresh = async () => {
    setIsRefreshing(true);
    
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['dlq-stats'] }),
      queryClient.invalidateQueries({ queryKey: ['dlq-items'] }),
      queryClient.invalidateQueries({ queryKey: ['dlq-count'] }),
      queryClient.invalidateQueries({ queryKey: ['dlq-contracts-stats'] }),
      queryClient.invalidateQueries({ queryKey: ['dlq-contracts-items'] }),
    ]);
    
    setLastUpdate(new Date());
    setIsRefreshing(false);
  };

  // Função para descartar itens antigos
  const handleDiscardOld = async () => {
    try {
      const days = parseInt(discardDays, 10);
      const count = await discardOldMutation.mutateAsync(days);
      
      if (count > 0) {
        toast.success(`${count} itens antigos descartados`);
      } else {
        toast.info('Nenhum item antigo encontrado');
      }
    } catch (error) {
      toast.error('Erro ao descartar itens antigos');
    }
  };

  // Formatar tempo desde última atualização
  const getTimeSinceUpdate = () => {
    const seconds = Math.floor((new Date().getTime() - lastUpdate.getTime()) / 1000);
    if (seconds < 60) return `há ${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    return `há ${minutes}min`;
  };

  return (
    <div className="space-y-6">
      {/* Cabeçalho */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Skull className="h-8 w-8 text-destructive" />
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Dead Letter Queue</h1>
            <p className="text-muted-foreground">
              Mensagens que falharam definitivamente no processamento
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Clock className="h-4 w-4" />
            <span>Atualizado {getTimeSinceUpdate()}</span>
          </div>
          
          {/* Ação para descartar antigos (apenas na aba Flows) */}
          {activeTab === 'flows' && (
            <div className="flex items-center gap-2">
              <Select value={discardDays} onValueChange={setDiscardDays}>
                <SelectTrigger className="w-[130px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">+ de 1 dia</SelectItem>
                  <SelectItem value="7">+ de 7 dias</SelectItem>
                  <SelectItem value="15">+ de 15 dias</SelectItem>
                  <SelectItem value="30">+ de 30 dias</SelectItem>
                </SelectContent>
              </Select>
              
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" size="sm" disabled={discardOldMutation.isPending}>
                    <Trash2 className="h-4 w-4 mr-2" />
                    Descartar Antigos
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Descartar itens antigos?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Todos os itens na DLQ com mais de {discardDays} dias serão 
                      arquivados permanentemente. Esta ação não pode ser desfeita.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancelar</AlertDialogCancel>
                    <AlertDialogAction onClick={handleDiscardOld}>
                      Descartar
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          )}
          
          <Button 
            onClick={handleRefresh} 
            disabled={isRefreshing}
            variant="outline"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
        </div>
      </div>

      {/* Tabs para alternar entre Flows e Contratos */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList>
          <TabsTrigger value="flows" className="gap-2">🔄 Flows</TabsTrigger>
          <TabsTrigger value="contracts" className="gap-2">📄 Contratos</TabsTrigger>
        </TabsList>

        {/* Aba Flows (conteúdo original) */}
        <TabsContent value="flows" className="space-y-6">
          <DlqStatusCards />
          <DlqHealthAlert />
          <div className="grid gap-6 md:grid-cols-2">
            <DlqReasonChart />
            <div className="hidden md:block" />
          </div>
          <DlqItemsTable />
        </TabsContent>

        {/* Aba Contratos */}
        <TabsContent value="contracts" className="space-y-6">
          <DlqContractsStatusCards />
          <DlqContractsItemsTable />
        </TabsContent>
      </Tabs>
    </div>
  );
}
